import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { full_name: "", phone: "", email: "", service: "", message: "" };

export default function ContactForm({ data, site }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const services = data?.services_options || ["Moving Services", "Cleaning Services", "Junk Removal", "Lock Installation", "TV Mounting", "Other"];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Could not send. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 px-6 bg-[#FBF8F1]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          {data?.eyebrow && (
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#B8941F] font-semibold mb-4">{data.eyebrow}</p>
          )}
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] leading-[1.1]">
            {data?.title_1} {data?.title_accent && <span className="text-gradient-gold">{data.title_accent}</span>}
          </h2>
          {data?.description && (
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">{data.description}</p>
          )}

          <div className="mt-10 space-y-5">
            <a href={site?.phone_href || "#"} data-testid="contact-info-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-2xl bg-white border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#FBF8F1] transition-colors shadow-sm">
                <Phone className="w-5 h-5 text-[#B8941F]" />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Call us</div>
                <div className="font-display text-lg font-bold text-[#0A0A0A] group-hover:text-[#B8941F] transition-colors">{site?.phone}</div>
              </div>
            </a>
            <a href={site?.email_href || "#"} data-testid="contact-info-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-2xl bg-white border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#FBF8F1] transition-colors shadow-sm">
                <Mail className="w-5 h-5 text-[#B8941F]" />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Email</div>
                <div className="font-display text-lg font-bold text-[#0A0A0A] group-hover:text-[#B8941F] transition-colors break-all">{site?.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-white border border-[#D4AF37]/30 flex items-center justify-center shadow-sm">
                <MapPin className="w-5 h-5 text-[#B8941F]" />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Service Area</div>
                <div className="font-display text-lg font-bold text-[#0A0A0A]">{data?.service_area_label || "Greater Boston, MA"}</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          data-testid="quote-form"
          className="lg:col-span-7 bg-white border border-[#EFEAD8] rounded-3xl p-7 sm:p-10 relative shadow-[0_24px_60px_-24px_rgba(0,0,0,0.15)]"
        >
          <div className="absolute -top-12 -right-6 w-40 h-40 rounded-full bg-[#D4AF37]/15 blur-[60px] pointer-events-none" />

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600 mb-2 block">Full Name *</label>
              <input type="text" value={form.full_name} onChange={update("full_name")} data-testid="form-full-name" className="input-luxe" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-600 mb-2 block">Phone Number *</label>
              <input type="tel" value={form.phone} onChange={update("phone")} data-testid="form-phone" className="input-luxe" placeholder="(555) 123-4567" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-gray-600 mb-2 block">Email Address *</label>
              <input type="email" value={form.email} onChange={update("email")} data-testid="form-email" className="input-luxe" placeholder="you@example.com" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-gray-600 mb-2 block">Service Needed *</label>
              <select
                value={form.service} onChange={update("service")}
                data-testid="form-service"
                className="input-luxe appearance-none cursor-pointer"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23D4AF37' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1.1rem center" }}
              >
                <option value="" disabled>Select a service…</option>
                {services.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-gray-600 mb-2 block">Message</label>
              <textarea rows={4} value={form.message} onChange={update("message")} data-testid="form-message" className="input-luxe resize-none" placeholder="Tell us a little about your project…" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="form-submit"
            className="btn-gold w-full mt-7 !py-4 !text-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending…</>) : (<>{data?.submit_label || "Get My Free Quote"}<Send className="w-4 h-4" /></>)}
          </button>

          <p className="mt-4 text-center text-xs text-gray-500">We respect your privacy. No spam, ever.</p>
        </motion.form>
      </div>
    </section>
  );
}
